/**
 * Registration page.
 * See wireframe: section 2.2 Registration Screen
 */

export function RegisterPage(): HTMLElement {
  const page = document.createElement('div');
  page.className = 'login-page';

  page.innerHTML = `
    <div class="auth-container">
      <div class="auth-card">
        <div class="auth-brand">
          <span class="auth-logo">ROOFER</span>
          <p class="auth-tagline">Create Your Account</p>
        </div>

        <form id="register-form" class="auth-form" novalidate>
          <div class="form-group">
            <label for="name">Full Name</label>
            <input type="text" id="name" name="ownerName" autocomplete="name" required />
            <span class="text-danger" id="name-error" role="alert"></span>
          </div>

          <div class="form-group">
            <label for="company">Company Name</label>
            <input type="text" id="company" name="tenantName" autocomplete="organization" required />
            <span class="text-danger" id="company-error" role="alert"></span>
          </div>

          <div class="form-group">
            <label for="subdomain">Subdomain</label>
            <div style="display:flex;align-items:center;gap:4px;">
              <input type="text" id="subdomain" name="subdomain" placeholder="my-company" required style="flex:1;" />
              <span style="color:var(--color-text-secondary);font-size:0.875rem;">.roofer.com</span>
            </div>
            <span class="text-danger" id="subdomain-error" role="alert"></span>
          </div>

          <div class="form-group">
            <label for="email">Email Address</label>
            <input type="email" id="email" name="ownerEmail" autocomplete="email" required />
            <span class="text-danger" id="email-error" role="alert"></span>
          </div>

          <div class="form-group">
            <label for="password">Password</label>
            <input type="password" id="password" name="ownerPassword" autocomplete="new-password" required />
            <span class="text-danger" id="password-error" role="alert"></span>
          </div>

          <div class="form-group">
            <label for="confirm-password">Confirm Password</label>
            <input type="password" id="confirm-password" name="confirmPassword" autocomplete="new-password" required />
            <span class="text-danger" id="confirm-error" role="alert"></span>
          </div>

          <div class="form-group">
            <label for="region">Region</label>
            <select id="region" name="region">
              <option value="US">United States</option>
              <option value="UK">United Kingdom</option>
              <option value="AU">Australia</option>
            </select>
          </div>

          <span class="text-danger" id="form-error" role="alert" style="margin-bottom:8px;display:block;"></span>

          <button type="submit" class="btn btn-primary btn-lg auth-submit" id="register-btn">
            Create Account
          </button>
        </form>

        <p class="auth-footer">
          Already have an account?
          <a href="#/login" class="auth-link">Log in</a>
        </p>
      </div>
    </div>
  `;

  attachRegisterHandlers(page);
  return page;
}

function attachRegisterHandlers(page: HTMLElement): void {
  const form = page.querySelector<HTMLFormElement>('#register-form')!;
  const nameInput = page.querySelector<HTMLInputElement>('#name')!;
  const companyInput = page.querySelector<HTMLInputElement>('#company')!;
  const subdomainInput = page.querySelector<HTMLInputElement>('#subdomain')!;
  const emailInput = page.querySelector<HTMLInputElement>('#email')!;
  const passwordInput = page.querySelector<HTMLInputElement>('#password')!;
  const confirmInput = page.querySelector<HTMLInputElement>('#confirm-password')!;
  const regionSelect = page.querySelector<HTMLSelectElement>('#region')!;

  const nameError = page.querySelector<HTMLSpanElement>('#name-error')!;
  const companyError = page.querySelector<HTMLSpanElement>('#company-error')!;
  const subdomainError = page.querySelector<HTMLSpanElement>('#subdomain-error')!;
  const emailError = page.querySelector<HTMLSpanElement>('#email-error')!;
  const passwordError = page.querySelector<HTMLSpanElement>('#password-error')!;
  const confirmError = page.querySelector<HTMLSpanElement>('#confirm-error')!;
  const formError = page.querySelector<HTMLSpanElement>('#form-error')!;
  const submitBtn = page.querySelector<HTMLButtonElement>('#register-btn')!;

  // Auto-generate subdomain from company name
  companyInput.addEventListener('input', () => {
    if (!subdomainInput.dataset.manual) {
      subdomainInput.value = companyInput.value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    }
  });

  subdomainInput.addEventListener('input', () => {
    subdomainInput.dataset.manual = 'true';
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    // Clear errors
    [nameError, companyError, subdomainError, emailError, passwordError, confirmError, formError].forEach(
      el => (el.textContent = ''),
    );

    const ownerName = nameInput.value.trim();
    const tenantName = companyInput.value.trim();
    const subdomain = subdomainInput.value.trim().toLowerCase();
    const ownerEmail = emailInput.value.trim();
    const ownerPassword = passwordInput.value;
    const confirm = confirmInput.value;
    const region = regionSelect.value as 'US' | 'UK' | 'AU';

    let valid = true;
    if (!ownerName) { nameError.textContent = 'Full name is required.'; valid = false; }
    if (!tenantName) { companyError.textContent = 'Company name is required.'; valid = false; }
    if (!subdomain) { subdomainError.textContent = 'Subdomain is required.'; valid = false; }
    else if (subdomain.length < 3) { subdomainError.textContent = 'Subdomain must be at least 3 characters.'; valid = false; }
    else if (!/^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(subdomain) && subdomain.length >= 2) {
      subdomainError.textContent = 'Subdomain must be lowercase letters, numbers, and hyphens only.'; valid = false;
    }
    if (!ownerEmail) { emailError.textContent = 'Email is required.'; valid = false; }
    if (!ownerPassword) { passwordError.textContent = 'Password is required.'; valid = false; }
    else if (ownerPassword.length < 8) { passwordError.textContent = 'Password must be at least 8 characters.'; valid = false; }
    else if (!/[A-Z]/.test(ownerPassword)) { passwordError.textContent = 'Password must contain at least one uppercase letter.'; valid = false; }
    else if (!/[a-z]/.test(ownerPassword)) { passwordError.textContent = 'Password must contain at least one lowercase letter.'; valid = false; }
    else if (!/[0-9]/.test(ownerPassword)) { passwordError.textContent = 'Password must contain at least one digit.'; valid = false; }
    if (ownerPassword !== confirm) { confirmError.textContent = 'Passwords do not match.'; valid = false; }
    if (!valid) return;

    submitBtn.disabled = true;
    submitBtn.textContent = 'Creating account\u2026';

    try {
      const { authApi } = await import('@/api/authApi');
      const response = await authApi.register({ tenantName, subdomain, region, ownerName, ownerEmail, ownerPassword });

      // Store tokens and redirect to dashboard (auto-login)
      const { setTokens } = await import('@/api/apiClient');
      setTokens(response.accessToken, response.refreshToken);
      window.location.hash = '#/dashboard';
    } catch (err) {
      formError.textContent =
        err instanceof Error ? err.message : 'Registration failed. Please try again.';
      submitBtn.disabled = false;
      submitBtn.textContent = 'Create Account';
    }
  });
}
